import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SearchRunway from "./SearchRunway";
import LoadingCircle from "../../utils/LoadingCircle";
import CardImageDetail from "./CardImageDetail";
import { HttpUnAuthApi } from "../../interface/unauth-api";

import { saveAs } from "file-saver";
import JSZip, { filter } from "jszip";
import { useWindowSize } from "../../utils/useWindowSize";

const unAuthAPI = new HttpUnAuthApi();

const Runway = () => {
  const toastRef: any = useRef();
  const { season, designer } = useParams();
  const windowSize = useWindowSize();
  const isNarrow = (windowSize?.width ?? 0) <= 1023; // 1024 미만이면 모바일 취급
  const [runwayList, setRunwayList] = useState<any>([]);
  const [alikeList, setAlikeList] = useState<any>([]);
  const [selectedImages, setSelectedImages] = useState<any>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedImage, setSelectedImage] = useState<string>();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // 현재 선택된 이미지의 인덱스
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 열림 상태
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  // 컴포넌트 상단
  const touchStart = useRef<{x:number; y:number} | null>(null);
  const touchMoved = useRef(false);
  const isTouchDevice = typeof window !== "undefined" && navigator.maxTouchPoints > 0;

  const SWIPE_THRESHOLD = 48; // px, 필요시 조절

  const handleTouchStart: React.TouchEventHandler<HTMLImageElement> = (e) => {
    if (!isTouchDevice) return;
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    touchMoved.current = false;
    // 모달 닫힘 방지 (상위 onClick 전파 금지)
    e.stopPropagation();
  };

  const handleTouchMove: React.TouchEventHandler<HTMLImageElement> = (e) => {
    if (!isTouchDevice || !touchStart.current) return;
    touchMoved.current = true;
    // 스크롤/바운스와 충돌 줄이려면 필요 시 기본동작 취소
    // e.preventDefault(); // (필요할 때만; Safari에서 과하게 막으면 역효과)
    e.stopPropagation();
  };

  const handleTouchEnd: React.TouchEventHandler<HTMLImageElement> = (e) => {
    if (!isTouchDevice || !touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;

    // 세로 이동이 가로보다 크고, 임계치 초과 시만 전환
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > SWIPE_THRESHOLD) {
      if (dy < 0) {
        // 위로 스와이프 → 다음
        if (selectedImageIndex < runwayList.length - 1) {
          handleNextClick();
        }
      } else {
        // 아래로 스와이프 → 이전
        if (selectedImageIndex > 0) {
          handlePrevClick();
        }
      }
    }

    touchStart.current = null;
    e.stopPropagation();
  };

  useEffect(() => {
    checkLastList();
    if (designer) {
      get_alike_runway_list();
    }
  }, [season, designer]);

  useEffect(() => {
    console.log("alikeList: ", alikeList);
  }, [alikeList]);

  useEffect(() => {
    if (isAllSelected) {
      setSelectedImages(runwayList);
    } else {
      setSelectedImages([]);
    }
  }, [isAllSelected]);

  useEffect(() => {
    console.log("selectedImages : ", selectedImages);
  }, [selectedImages]);

  useEffect(() => {
    setSelectedImage(runwayList[selectedImageIndex]);
  }, [selectedImageIndex]);

  const checkLastList = () => {
    // 쿠키에서 마지막 이미지 리스트를 조회
    const lastListStr = window.localStorage.getItem("selectedImgList");
    console.log("lastListStr : ", lastListStr);

    const lastList = lastListStr?.split(',');
    const sortedLastList = sortedList(lastList);

    setRunwayList(sortedLastList);
  };

  // 정렬 우선순위 매핑
  const orderMap: Record<string, number> = {
    collection: 1,
    detail: 2,
    beauty: 3,
  };

  const sortedList = (arr: any) => {
    const sortedArr = arr.sort((a: any, b: any) => {
      const getPriority = (url: string) => {
        const lower = url.toLowerCase();
        if (lower.includes("collection")) return orderMap.collection;
        if (lower.includes("detail")) return orderMap.detail;
        if (lower.includes("beauty")) return orderMap.beauty;
        return 999;
      };

      return getPriority(a) - getPriority(b);
    })

    return sortedArr;
  }

  const selectImage = (src: string) => {
    let temp = [...selectedImages];
    const index = temp.indexOf(src);
    if (index > -1) {
      temp.splice(index, 1);
    } else {
      temp.push(src);
    }
    setSelectedImages(temp);
  };

  const selectAllImages = () => {
    setIsAllSelected(!isAllSelected);
  };

  const getImgUrl = async (imgKey: string) => {
    const param = {
      command: "get_presigned_url",
      key: imgKey
    };

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      return res.response.url;
    }
  };

  const get_alike_runway_list = async () => {
    const param: any = {
      command: "get_runway_search_list",
      season: season,
      designer: designer
    };

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      getFilteredRunwayList(res.response.runway_list);
    } else {
      console.error("[ERROR] get_alike_runway_list: ", res.response.error_msg);
    }
  };

  const getFilteredRunwayList = (runwayList: any) => {
    if (!runwayList) return;

    const groupMap = new Map<string, any>();

    for (const item of runwayList) {
      const key = `${item.season}__${item.designer}`;

      if (!groupMap.has(key)) {
        // 최초 삽입
        groupMap.set(key, { ...item, imglist: [...(item.imglist || [])] });
      } else {
        const existing = groupMap.get(key);

        // imglist 통합 (중복 제거)
        const combinedImgList = Array.from(
          new Set([...(existing.imglist || []), ...(item.imglist || [])])
        );

        // 문자열 컬럼 병합
        const merged = {
          ...existing,
          ...item,
          imglist: combinedImgList,
          // 처럼 이어붙일 수 있는 필드들
          imgtype: [existing.imgtype, item.imgtype].filter(Boolean).join(','),
          gender: [existing.gender, item.gender].filter(Boolean).join(','),
          city: [existing.city, item.city].filter(Boolean).join(','),
        };

        groupMap.set(key, merged);
      }
    }

    console.log("groupMap: ", groupMap);
    const filteredList = Array.from(groupMap.values());
    console.log("filteredList: ", filteredList);

    filteredList.sort((a: any, b: any) => {
      if (a.sk > b.sk) return -1;
      if (a.sk < b.sk) return 1;
      return 0;
    });

    setAlikeList(filteredList);
  };

  const sanitizeFileName = (input: any): string => {
    // 파일명에서 사용할 수 없는 문자들을 안전하게 치환
    return input.replace(/[\/\\:*?"<>|]/g, "_");
  };

  const fnDownloadCount = async () => {
    // 요청 배열 생성 - 시즌, 디자이너 같이 들어감.
    const param = {
      command: "update_download_count",
      selectedKey: season+"#"+ designer,
    };
    const res = await unAuthAPI.post(param);
    if(res.code==="200"){}
  };

  const fncDownloadAttach = async () => {
    const safeSeason = sanitizeFileName(season);
    const safeDesigner = sanitizeFileName(designer);

    if (selectedImages.length === 1) {
      try {
        const res = await fetch(selectedImages[0]);
        if (!res.ok) throw new Error("Failed to fetch image");

        const blob = await res.blob();
        const fileName = `${safeSeason}_${safeDesigner}_01.jpg`;
        saveAs(blob, fileName);
      } catch (error) {
        console.error("단일 이미지 다운로드 오류 : ", error);
      }
    } else {
      const zip = new JSZip();

      try {
        await Promise.all(
          selectedImages.map(async (url: string, index: number) => {
            const res = await fetch(url);
            if (!res.ok) throw new Error("Failed to fetch image");
  
            const blob = await res.blob();
            const fileName = `${safeSeason}_${safeDesigner}_${String(index + 1).padStart(2, "0")}.jpg`;
            
            // 파일명을 zip에 추가
            zip.file(fileName, blob);
          })
        );
  
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipFileName =  `${safeSeason}_${safeDesigner}.zip`;
        saveAs(zipBlob, zipFileName);
        fnDownloadCount()
      } catch (error) {
        console.error("ZIP 다운로드 중 오류 발생: ", error);
      }
    }
  };

  const toPrintSafeURL = (url: string) => {
    // 이미 인코딩된 %를 이중 인코딩하지 않도록 encodeURI 사용
    let u = encodeURI(url);
    // #가 있으면 브라우저가 fragment로 처리 → 실제 요청 실패 가능
    u = u.replace(/#/g, "%23");
    return u;
  };

  // 필요시 title 전달 가능: buildPrintHTML(urls, "2025SS / Dior")
  const buildPrintHTML = (urls: string[]) => {
    // titleText 조합
    let titleText = "";
    if (season && designer) {
      titleText = `${season}_${designer}`;
    } else if (season) {
      titleText = season;
    } else if (designer) {
      titleText = designer;
    }
  
    return `
    <!doctype html>
    <html>
    <head>
    <meta charset="utf-8" />
    <title>AI TB</title>
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      html, body { margin: 0; padding: 0; }
      .page {
        width: 186mm; height: 273mm;
        box-sizing: border-box;
        display: block;
        break-after: page;
        page-break-after: always;
        overflow: hidden;
      }
      .title {
        width: 186mm; height: 273mm;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        font-family: system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Arial;
      }
      .title h1 { margin: 0 0 8mm; font-size: 24pt; }
      .title p  { margin: 0; font-size: 12pt; color: #555; }
      .figure { display: flex; flex-direction: column; height: 100%; min-height: 0; }
      .img-area { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; }
      img.print-img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
      .caption {
        height: 12mm; line-height: 12mm; text-align: center;
        font-size: 11pt; color: #333;
        font-family: system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Arial;
        box-sizing: border-box; margin: 0;
      }
    </style>
    </head>
    <body>
      ${
        titleText
          ? `<div class="page title">
              <h1>${titleText}</h1>
              <p>Generated ${new Date().toLocaleDateString("ko-KR")}</p>
            </div>`
          : ""
      }
      ${urls.slice().sort((a, b) => a.localeCompare(b)).map(
          (u, idx) => `
          <div class="page">
            <div class="figure">
              <div class="img-area">
                <img class="print-img" src="${toPrintSafeURL(u)}" />
              </div>
              <div class="caption">
                ${titleText ? `${titleText}` : ``}
              </div>
            </div>
          </div>
        `
        )
        .join("")}
    </body>
    </html>`;
  };
  



  
  // 이미지가 실제로 로드됐는지 확인 (iframe 내부 문서 기준)
  const waitAllImagesLoaded = (doc: Document) => {
    const imgs = Array.from(doc.images) as HTMLImageElement[];
    if (imgs.length === 0) return Promise.resolve();
  
    const promises = imgs.map(
      (img) =>
        new Promise<void>((resolve, reject) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.addEventListener("load", () => resolve(), { once: true });
          img.addEventListener("error", () => reject(new Error(`이미지 로드 실패: ${img.src}`)), { once: true });
        })
    );
    return Promise.all(promises).then(() => undefined);
  };
  
  const fncPrint = async () => {
    if (!selectedImages || selectedImages.length === 0) {
      toastRef?.current?.toast("인쇄할 이미지가 없습니다.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }
  
    // 1) hidden iframe 생성
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
  
    const win = iframe.contentWindow!;
      const doc = iframe.contentDocument || win.document;
    if (!win || !doc) {
      console.error("인쇄 iframe 초기화 실패");
      document.body.removeChild(iframe);
      return;
    }
  
    // 2) 인쇄용 HTML 주입 (모든 이미지 한 문서에 작성)
    doc.open();
    doc.write(buildPrintHTML(selectedImages));
    doc.close();
  
    try {
      // 3) 문서/이미지 로드 완료 대기
      await new Promise<void>((resolve) => {
        if (doc.readyState === "complete") resolve();
        else win.addEventListener("load", () => resolve(), { once: true });
      });
      await waitAllImagesLoaded(doc);
  
      // 4) 인쇄 실행 (단 한 번)
      win.focus();
      win.print();
    } catch (e) {
      console.error("인쇄 중 오류:", e);
    } finally {
      // 5) 정리
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch {}
      }, 1000);
    }
  };
  
  



  // 전체 보기 버튼 클릭 시 모달 열기
  const handleViewAllClick = (index: number, imgKey: string) => {
    setIsModalOpen(true);
    setSelectedImage(imgKey);
    setSelectedImageIndex(index);
  };

  // 모달 외부 클릭 시 모달 닫기
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleImageLoad = () => {
    if (imgRef.current) {
      setImageWidth(imgRef.current.clientWidth); // 이미지가 로드된 후 너비 설정
      setImageHeight(imgRef.current.clientHeight);
    }
  };

  // 화살표 클릭 시 이미지 변경
  const handlePrevClick = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNextClick = () => {
    if ( runwayList.length > 0 && selectedImageIndex < runwayList.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  return (
    <>
      <section id="commWrap">
        <article className="article-01">
          <div
            style={{
              fontWeight: '500',
              fontSize: '18px',
              color: '#8A8A8A',
              marginBottom: '20px',
            }}
          >
            {season}
          </div>
          <div className="article-tit" style={{ marginBottom: '80px' }}>
            {designer}
          </div>
          <Stack direction="column" gap={15}>
            <Stack direction="column" gap={3} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <div
                    style={{ 
                      width: '25px',
                      height: '25px',
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`checkImgAll`}
                      className="check-brief"
                      onClick={selectAllImages}
                      style={{ cursor: 'pointer', display: 'none' }}
                    />
                    <label 
                      htmlFor={`checkImgAll`}
                      style={{ display: 'block', width: '100%', height: '100%', cursor: 'pointer' }}
                    ></label>
                  </div>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>전체 선택</Typography>
                </div>

                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                  {!isNarrow&&
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor:'pointer'}}
                      onClick={fncPrint}
                    >
                      <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>인쇄하기</Typography>
                      <img src="/images/icon-print-18.png" style={{ width: '18px', height: '18px' }}/>
                    </Box>
                  }
                  

                  <Box 
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor:"pointer" }}
                    onClick={fncDownloadAttach}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '16px'}}>다운로드</Typography>
                    <img src="/images/icon-download-18.png" style={{ width: '18px', height: '18px' }}/>
                  </Box>
                </Box>
              </Box>

              <div style={{ width: '100%' }}>
                <dl>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                    {runwayList.length > 0 && 
                      runwayList.map((key: any, index: number) => {
                        // console.log("😂 : ",key)
                        return(
                        <CardImageDetail
                          key={index}
                          index={index}
                          runwayImg={key}
                          season={season ?? ""}
                          designer={designer ?? ""}
                          checked={selectedImages.indexOf(key) > -1}
                          callback={(payload: any) => {
                            if (payload.command === "selectImage") {
                              selectImage(payload.key);
                            } else if (payload.command === "showFullScreenImg") {
                              handleViewAllClick(payload.index, payload.key)
                            }
                          }}
                        />
                      )})
                    }
                    {/* 간격을 위한 더미 채우기 */}
                    {(() => {
                      const cols =windowSize.width >= 1024 ? 4 : windowSize.width >= 640 ? 3 : 2;
                      const cardWidth = windowSize.width >= 1024? "23%"
                          : windowSize.width >= 640
                          ? "31.3%"
                          : "48%";
                      const len = runwayList?.length ?? 0;
                      const remainder = len % cols;
                      const dummyCount = remainder === 0 ? 0 : cols - remainder;

                      return Array.from({ length: dummyCount }).map((_, i) => (
                        <div
                          key={`dummy-${i}`}
                          style={{
                            width: cardWidth,     // 실 카드와 동일 폭
                            marginBottom: "48px", // 동일 마진
                            visibility: "hidden", // 공간만 차지
                          }}
                        />
                      ));
                    })()}
                  </div>
                </dl>
              </div>
            </Stack>
          </Stack>
          <SearchRunway 
            searchList={alikeList}
          />
        </article>
      </section>
      
      {isModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,

            // ✅ 수직 기본 스크롤 억제(우린 세로 제스처를 자체 처리)
            // 필요에 따라 'none'으로 더 강하게 막아도 됨
            touchAction: 'pan-x',
          }}
          onClick={handleModalClose} // 모달 외부 클릭 시 닫기
        >
          <img
            src={selectedImage}
            ref={imgRef} 
            onLoad={handleImageLoad}
            alt="전체 이미지"
            style={{
              maxWidth: '85%',
              maxHeight: '85%',
            }}
            // ↓↓↓ 모바일 스와이프
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            // 마우스 드래그 오작동 방지(데스크톱)
            draggable={false}
          />
          
          {/* 전체 보기 버튼을 대표 이미지 오른쪽 위에 추가 */}
          <button
            onClick={handleModalClose}
            style={{
              position: 'absolute',
              top: `calc(50% - ${imageHeight / 2 - 24}px)`, // 위쪽 여백
              right: `calc(50% - ${imageWidth / 2 - 24}px)`, // 오른쪽 여백
              // backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '5px',
              width: '32px',
              height: '32px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src="/images/btn-minimize-screen.png" alt="" />
          </button>
            
          {/* 모달 내 화살표 추가 */}
          {runwayList.length > 1 && 
            <>
            <Box
              sx={{
                position: 'absolute',
                left: `calc(50% - ${imageWidth / 2 + 40 + 35}px)`, // 왼쪽 여백
                top: '50%', // 수직 중앙 정렬
                transform: 'translateY(-50%)', // 수직 중앙 정렬을 위한 변환
              }}
            >
              <button
                className="swiper-button-prev"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  handlePrevClick(); 
                }}
                style={{ 
                  backgroundColor: 'rgb(0, 0, 0)', 
                  border: 'none', 
                  borderRadius: '50px', 
                  padding: '8px', 
                  opacity: selectedImageIndex === 0 ? 0.5 : 1, 
                  cursor: 'pointer',
                   width: '40px', 
                   height: '40px', 
                   transform: 'none' 
                }}
                disabled={selectedImageIndex === 0} // 첫 번째 이미지일 경우 비활성화
              >
                <img src="/images/icon-arr-prev.png" alt="" style={{ width: "10px", height: "15px" }}/>
              </button>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                left: `calc(50% + ${imageWidth / 2 - 40 - 10}px)`, // 오른쪽 여백
                top: '50%', // 수직 중앙 정렬
                transform: 'translateY(-50%)', // 수직 중앙 정렬을 위한 변환
              }}
            >
              <button
                className="swiper-button-next"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  handleNextClick(); 
                }}
                style={{ 
                  backgroundColor: 'rgb(0, 0, 0)', 
                  border: 'none', 
                  borderRadius: '50px', 
                  padding: '8px', 
                  opacity: selectedImageIndex === runwayList.length - 1 ? 0.5 : 1, 
                  cursor: 'pointer', 
                  width: '40px',
                  height: '40px', 
                  left: "70px", 
                  transform: 'none' 
                }}
                disabled={selectedImageIndex === runwayList.length - 1} // 마지막 이미지일 경우 비활성화
              >
                <img src="/images/icon-arr-next.png" alt="" style={{ width: "10px", height: "15px" }} />
              </button>
            </Box>
            </>
          }
        </Box>
      )}
      <LoadingCircle loading={isLoading} />
    </>
  )
}

export default Runway;